import { AuthenticatedTemplate, UnauthenticatedTemplate, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { FluentProvider, makeStyles, shorthands, tokens, Toaster } from '@fluentui/react-components';

import * as React from 'react';
import { useCallback, useEffect } from 'react';
import Chat from './components/chat/Chat';
import { Loading, Login } from './components/views';
import { AuthHelper } from './libs/auth/AuthHelper';
import { useChat, useFile } from './libs/hooks';
import { AlertType } from './libs/models/AlertType';
import { useAppDispatch, useAppSelector } from './redux/app/hooks';
import { RootState, store } from './redux/app/store';
import { FeatureKeys } from './redux/features/app/AppState';
import { addAlert, setActiveUserInfo, setServiceInfo, setTokenReadiness } from './redux/features/app/appSlice';
import {
    ensureHubConnectionStarted,
    setHubAccessTokenProvider,
} from './redux/features/message-relay/signalRHubConnection';
import { semanticKernelDarkTheme, semanticKernelLightTheme } from './styles';
import { AgentService, AgentSelectListItem } from './libs/services/AgentService';
import { azureToken } from './components/views/AzureToken';
import { httpInterceptor } from './libs/services/HttpInterceptorService';

export const useClasses = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100%',
        ...shorthands.overflow('hidden'),
    },
    header: {
        alignItems: 'center',
        backgroundColor: tokens.colorBrandForeground2,
        color: tokens.colorNeutralForegroundOnBrand,
        display: 'flex',
        '& h1': {
            paddingLeft: tokens.spacingHorizontalXL,
            display: 'flex',
        },
        height: '48px',
        justifyContent: 'space-between',
        width: '100%',
    },
    persona: {
        marginRight: tokens.spacingHorizontalXXL,
    },
    cornerItems: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        ...shorthands.gap(tokens.spacingHorizontalS),
    },
});

export enum AppState {
    ProbeForBackend,
    SettingUserInfo,
    ErrorLoadingChats,
    ErrorLoadingUserInfo,
    LoadChats,
    LoadingChats,
    Chat,
    SigningOut,
}

const App = () => {
    const classes = useClasses();
    const [appState, setAppState] = React.useState(AppState.ProbeForBackend);
    const dispatch = useAppDispatch();
    const { instance, inProgress } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const { features, isMaintenance } = useAppSelector((state: RootState) => state.app);

    const chat = useChat();
    const file = useFile();

    const [agents, setAgents] = React.useState<AgentSelectListItem[]>([]);
    const [teamsToken, setTeamsToken] = React.useState<string>('');
    const { tokenReadiness } = useAppSelector((state: RootState) => state.app);

    const handleAppStateChange = useCallback((newState: AppState) => {
        setAppState(newState);
    }, []);

    const fetchAgents = useCallback(async () => {
        try {
            const agentService = new AgentService();
            const data = await agentService.getAgentSelectListItems(
                'agents/select-list',
                await AuthHelper.getSKaaSAccessToken(instance, inProgress),
            );

            setAgents(data);
        } catch (error) {
            console.error('Ajan listesi yüklenirken bir hata oluştu:', error);
            dispatch(
                addAlert({
                    message: 'Ajanlar yüklenirken bir hata oluştu. Lütfen yeniden deneyin.',
                    type: AlertType.Error,
                }),
            );
        }
    }, [instance, inProgress, dispatch]);

    // Teams token'i almak için useEffect - SADECE BİR KEZ ÇALIŞIR (29bc1da tarzı)
    useEffect(() => {
        // Token zaten varsa tekrar alma
        if (teamsToken) {
            return;
        }

        // JWT token payload tipi
        interface JwtPayload {
            oid?: string;
            sub?: string;
            upn?: string;
            email?: string;
            preferred_username?: string;
            name?: string;
            [key: string]: unknown;
        }

        const parseJwtToken = (token: string): JwtPayload | null => {
            try {
                const base64Url = token.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const jsonPayload = decodeURIComponent(
                    atob(base64)
                        .split('')
                        .map((c) => {
                            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                        })
                        .join(''),
                );
                return JSON.parse(jsonPayload) as JwtPayload;
            } catch (error: unknown) {
                const errorMessage = error instanceof Error ? error.message : String(error);
                console.error('JWT parse error:', errorMessage);
                return null;
            }
        };

        const getTeamsToken = async () => {
            try {
                dispatch(setTokenReadiness('acquiring'));
                const token = await azureToken(['User.Read']);
                if (token) {
                    setTeamsToken(token);
                    httpInterceptor.setTeamsToken(token);

                    // JWT token'dan kullanıcı bilgisini çıkar
                    const tokenPayload = parseJwtToken(token);
                    if (tokenPayload) {
                        const userInfo = {
                            id: tokenPayload.oid ?? tokenPayload.sub ?? 'teams-user',
                            email:
                                tokenPayload.upn ??
                                tokenPayload.email ??
                                tokenPayload.preferred_username ??
                                'teams-user@teams.com',
                            username: tokenPayload.name ?? tokenPayload.preferred_username ?? 'Teams User',
                        };

                        dispatch(setActiveUserInfo(userInfo));
                    }

                    dispatch(setTokenReadiness('ready'));
                } else {
                    httpInterceptor.clearTokens();
                }
            } catch (error: unknown) {
                const errorMessage = error instanceof Error ? error.message : String(error);
                console.error('Teams token error:', errorMessage);
                // Hata olursa sessiz kal, MSAL devreye girecek
            }
        };

        void getTeamsToken();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Phase 2: Transition to LoadChats once Teams token is ready
    useEffect(() => {
        if (
            teamsToken &&
            appState !== AppState.Chat &&
            appState !== AppState.LoadChats &&
            appState !== AppState.LoadingChats
        ) {
            handleAppStateChange(AppState.LoadChats);
        }
    }, [teamsToken, appState, handleAppStateChange]);

    // Phase 3: Handle MSAL authentication and token readiness
    useEffect(() => {
        if (isMaintenance && appState !== AppState.ProbeForBackend) {
            handleAppStateChange(AppState.ProbeForBackend);
            return;
        }

        if (isAuthenticated && appState === AppState.SettingUserInfo) {
            const account = instance.getActiveAccount();
            if (!account) {
                handleAppStateChange(AppState.ErrorLoadingUserInfo);
                dispatch(setTokenReadiness('failed'));
            } else {
                // Mark token as acquiring, then fetch it
                dispatch(setTokenReadiness('acquiring'));

                // Acquire MSAL token
                void AuthHelper.getSKaaSAccessToken(instance, inProgress).then((token) => {
                    if (token) {
                        dispatch(setTokenReadiness('ready'));
                        
                        // Fetch user roles using shared helper with already-acquired token
                    } else {
                        dispatch(setTokenReadiness('failed'));
                    }
                });

                dispatch(
                    setActiveUserInfo({
                        id: account.localAccountId,
                        email: account.username.toUpperCase(),
                        username: account.name ?? account.username,
                    }),
                );

                if (account.username.split('@')[1] === 'microsoft.com') {
                    dispatch(
                        addAlert({
                            message:
                                'By using Chat Copilot, you agree to protect sensitive data, not store it in chat, and allow chat history collection for service improvements. This tool is for internal use only.',
                            type: AlertType.Info,
                        }),
                    );
                }

                handleAppStateChange(AppState.LoadChats);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [instance, isAuthenticated, appState, isMaintenance, dispatch]);

    useEffect(() => {
        if (tokenReadiness === 'ready' && agents.length === 0) {
            void fetchAgents();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tokenReadiness]);

    // The message relay hub requires authentication: connect only once the user is signed in
    // and hand it the same token source as the REST calls, so reconnects get a fresh token.
    useEffect(() => {
        const isAuthAAD = AuthHelper.isAuthAAD();
        setHubAccessTokenProvider(() => AuthHelper.getSKaaSAccessToken(instance, inProgress), isAuthAAD);

        const isSignedIn = isAuthenticated || teamsToken !== '';
        if (!isAuthAAD || (tokenReadiness === 'ready' && isSignedIn)) {
            ensureHubConnectionStarted(store);
        }
    }, [tokenReadiness, isAuthenticated, teamsToken, instance, inProgress]);

    // Sequential chat loading after agents are ready
    useEffect(() => {
        if ((isAuthenticated || !AuthHelper.isAuthAAD() || teamsToken) && appState === AppState.LoadChats) {
            // Wait for agents to load before proceeding
            if (agents.length === 0) {
                return;
            }

            handleAppStateChange(AppState.LoadingChats);
            void Promise.all([
                chat
                    .loadChats(agents[0]?.value)
                    .then(() => {
                        handleAppStateChange(AppState.Chat);
                    })
                    .catch((error: unknown) => {
                        const errorMessage = error instanceof Error ? error.message : String(error);
                        console.error('Error loading chats:', errorMessage);
                        handleAppStateChange(AppState.ErrorLoadingChats);
                    }),
                file.getContentSafetyStatus(),
                chat.getServiceInfo().then((serviceInfo) => {
                    if (serviceInfo) {
                        dispatch(setServiceInfo(serviceInfo));
                    }
                }),
            ]);
        } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [instance, isAuthenticated, appState, isMaintenance, teamsToken, agents]);

    const theme = features[FeatureKeys.DarkMode].enabled ? semanticKernelDarkTheme : semanticKernelLightTheme;

    return (
        <FluentProvider className="app-container" theme={theme}>
            <Toaster position="top" offset={{ horizontal: 0, vertical: 20 }} />
            {AuthHelper.isAuthAAD() && !teamsToken ? (
                <>
                    <UnauthenticatedTemplate>
                        <div className={classes.container}>
                            {appState === AppState.SigningOut && <Loading text="Çıkış yapılıyor..." />}
                            {appState !== AppState.SigningOut && <Login />}
                        </div>
                    </UnauthenticatedTemplate>
                    <AuthenticatedTemplate>
                        <Chat
                            classes={classes}
                            appState={appState}
                            setAppState={handleAppStateChange}
                            agents={agents}
                        />
                    </AuthenticatedTemplate>
                </>
            ) : (
                <Chat classes={classes} appState={appState} setAppState={handleAppStateChange} agents={agents} />
            )}
        </FluentProvider>
    );
};

export default App;
